import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useSocket } from 'contexts/SocketProvider';
import { useUser } from '@dfl/react-security';
import { useNotificationSound } from './useNotificationSound';
import { FileAttachment, IExportData, INotification, NOTIFICATION_TYPE } from '../interfaces/INotification';
import { clearExportState, getLastExportId, isExportModalOpen } from 'modules/export/storage/last-export.storage';
import { NotificationTypeEnum } from '../constants/notification.enum';
import { NOTIFICATION_COUNT_KEY, NOTIFICATION_USER_KEY } from '../constants/query';
import NotificationToastItem from '../components/NotificationDrawer/NotificationToastItem';
import notificationSound from '../../../assets/sounds/notification.mp3';
import { useExportModal } from 'contexts/ExportModalProvider';
import { useCentralizedExport } from 'contexts/CentralizedExportProvider';

/**
 * Global hook to manage notification socket connection.
 * (Hack to keep socket updated while notifications drawer not mounted)
 */
export const useNotificationSocket = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('notification');
  const { socket, isConnected } = useSocket();
  const { user } = useUser();
  const { isSoundEnabled } = useNotificationSound();
  const { currentExportId } = useExportModal();
  const { isModalOpen } = useCentralizedExport();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioReadyRef = useRef<boolean>(false);
  const playingRef = useRef<boolean>(false);

  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.volume = 0.5;

    const prepareAudio = () => {
      const audio = audioRef.current;
      if (audio && !audioReadyRef.current) {
        audio
          .play()
          .then(() => {
            audio.pause();
            audio.currentTime = 0;
            audioReadyRef.current = true;
          })
          .catch(() => {});
      }
    };

    prepareAudio();

    const events = ['click', 'keydown', 'touchstart'];
    events.forEach((event) => {
      document.addEventListener(event, prepareAudio);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, prepareAudio);
      });
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    if (!isSoundEnabled || playingRef.current) return;

    const audio = audioRef.current;
    if (!audio) return;

    playingRef.current = true;

    if (!audioReadyRef.current) {
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audioReadyRef.current = true;
          return audio.play();
        })
        .then(() => {
          audio.onended = () => {
            playingRef.current = false;
          };
        })
        .catch((error) => {
          console.warn('Failed to play notification sound after prep:', error);
          playingRef.current = false;
        });
    } else {
      audio.currentTime = 0;
      audio
        .play()
        .then(() => {
          audio.onended = () => {
            playingRef.current = false;
          };
        })
        .catch((error) => {
          console.warn('Failed to play notification sound:', error);
          playingRef.current = false;
        });
    }
  }, [isSoundEnabled]);

  const handleNotification = useCallback(
    (newNotification: INotification) => {
      // Skip notification if it was executed by the current user
      if (newNotification.executedBy?._id === user?._id) {
        return;
      }

      const notification = { ...newNotification, notificationKind: NotificationTypeEnum.notification };

      // Update all notification list queries
      queryClient.setQueriesData({ queryKey: [NOTIFICATION_USER_KEY] }, (oldData: any) => {
        if (!oldData?.data) {
          return {
            data: [notification],
            total: 1,
          };
        }

        const exists = oldData.data.some((n: INotification) => n._id === notification._id);
        if (exists) return oldData;

        return {
          ...oldData,
          data: [notification, ...oldData.data],
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          total: Number(oldData?.total || 0) + 1,
        };
      });

      // Update notification count
      queryClient.setQueryData([NOTIFICATION_COUNT_KEY], (oldCount: any) => {
        const currentCount: number =
          typeof oldCount === 'object' && oldCount !== null && 'count' in oldCount
            ? Number(oldCount?.count || 0)
            : Number(oldCount || 0);
        return currentCount + 1;
      });

      // Play notification sound
      playNotificationSound();

      // Show toast notification
      const toastId = `notification-${notification._id}`;
      toast.dismiss(toastId);

      toast.custom(
        (t) => (
          <NotificationToastItem
            notification={notification}
            toastId={toastId}
            visible={t.visible}
            onDismiss={() => {
              toast.dismiss(toastId);
            }}
          />
        ),
        {
          position: 'top-right',
          duration: 4000,
          id: toastId,
        },
      );
    },
    [playNotificationSound, queryClient, user?._id],
  );

  const handleExportDone = useCallback(
    (exportData: IExportData) => {
      // Websocket payload arrives as { type, data: { _id, name, path, status }, createdAt }
      const payload: any = exportData as any;
      const socketData: any = payload.data ?? payload;

      // Skip notification if it was executed by the current user
      if (payload.executedBy?._id === user?._id) {
        return;
      }

      // Normalize export id: it may come as _id or id either in data or at root
      const messageId: string | undefined = socketData?._id || socketData?.id || payload?._id || payload?.id;

      // File name: prioritize data.name, then fall back to root-level name
      const exportName: string = socketData?.name || payload?.name || 'export_file';

      // Build relative download path from the websocket payload
      const rawPath: string | undefined = socketData?.path || payload?.path;

      const getRelativeDownloadPath = (path?: string): string | null => {
        if (!path) return null;
        // If it already starts with "/", assume it's a complete relative path
        if (path.startsWith('/')) return path;
        // General case: complete path the same way as in sales export services
        return `/ms-sales/api/download/download/${path}`;
      };

      const getFileType = (filename: string): string => {
        const lower = filename.toLowerCase();
        if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
          return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        }
        if (lower.endsWith('.pdf')) {
          return 'application/pdf';
        }
        if (lower.endsWith('.docx') || lower.endsWith('.doc')) {
          return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        }
        if (lower.endsWith('.csv')) {
          return 'text/csv';
        }
        return 'application/octet-stream';
      };

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
      const relativePath = getRelativeDownloadPath(rawPath);
      const downloadUrl = relativePath ? `${baseUrl}${relativePath}` : undefined;

      const exportFile: FileAttachment | undefined = downloadUrl
        ? {
            url: downloadUrl,
            filename: exportName,
            type: getFileType(exportName),
          }
        : undefined;

      const notification: INotification = {
        _id: messageId || '',
        title: t('titles.EXPORT_DONE', { defaultValue: 'Export Completed' }),
        type: NOTIFICATION_TYPE.EXPORT_DONE,
        notificationKind: NotificationTypeEnum.file_export,
        message: exportName,
        data: {
          _id: messageId,
          name: exportName,
          ...payload.data,
          ...(exportFile
            ? {
                file: exportFile,
              }
            : {}),
        },
        executedBy: payload?.executedBy,
        important: false,
        createdAt: payload?.createdAt,
        viewed: false,
      };

      const lastExportId = getLastExportId();
      const currentId: string | undefined = messageId;
      const isActiveExport = lastExportId && currentId && lastExportId === currentId;
      const modalIsOpen = isExportModalOpen();

      // Check if this export matches the current export in the modal
      const isCurrentExport = currentExportId && currentId && currentExportId === currentId;

      // Update all notification list queries
      queryClient.setQueriesData({ queryKey: [NOTIFICATION_USER_KEY] }, (oldData: any) => {
        if (!oldData?.data) {
          return {
            data: [notification],
            total: 1,
          };
        }

        const exists = oldData?.data?.some((n: INotification) => n?._id === notification?._id);
        if (exists) return oldData;

        return {
          ...oldData,
          data: [notification, ...oldData.data],
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          total: Number(oldData?.total || 0) + 1,
        };
      });

      // Update notification count
      queryClient.setQueryData([NOTIFICATION_COUNT_KEY], (oldCount: any) => {
        const currentCount: number =
          typeof oldCount === 'object' && oldCount !== null && 'count' in oldCount
            ? Number(oldCount.count || 0)
            : Number(oldCount || 0);
        return currentCount + 1;
      });

      // If this is the current export and modal is open, DON'T show toast
      // The modal itself will handle the download and close automatically
      if (isCurrentExport && modalIsOpen) {
        // Don't show toast, don't clear state - let the modal handle it
        return;
      }

      // Show toast notification only if modal is closed and export modals are not open
      if (isActiveExport && !modalIsOpen && exportFile && !isModalOpen) {
        // Modal was closed manually, show toast and auto-download
        playNotificationSound();

        const toastId = `export-active-${notification._id}`;

        // First dismiss any existing toast with the same ID
        toast.dismiss(toastId);

        toast.custom(
          (t) => (
            <NotificationToastItem
              notification={notification}
              toastId={toastId}
              visible={t.visible}
              onDismiss={() => {
                toast.dismiss(toastId);
              }}
            />
          ),
          {
            position: 'top-right',
            duration: 4000,
            id: toastId,
          },
        );

        // Clear export state after processing
        clearExportState();
      } else if (!isActiveExport && !modalIsOpen && !isModalOpen) {
        // Different export, show toast normally
        playNotificationSound();

        const toastId = `export-other-${notification._id}`;

        // First dismiss any existing toast with the same ID
        toast.dismiss(toastId);

        toast.custom(
          (t) => (
            <NotificationToastItem
              notification={notification}
              toastId={toastId}
              visible={t.visible}
              onDismiss={() => {
                toast.dismiss(toastId);
              }}
            />
          ),
          {
            position: 'top-right',
            duration: 4000,
            id: toastId,
          },
        );
      }
    },
    [user?._id, t, queryClient, playNotificationSound, currentExportId, isModalOpen],
  );

  useEffect(() => {
    if (!socket) {
      return;
    }

    socket.on(NotificationTypeEnum.notification, handleNotification);
    socket.on(NotificationTypeEnum.file_export, handleExportDone);

    return () => {
      socket.off(NotificationTypeEnum.notification, handleNotification);
      socket.off(NotificationTypeEnum.file_export, handleExportDone);
    };
  }, [socket, queryClient, t, handleNotification, handleExportDone]);

  return {
    isConnected,
  };
};
